import React, { Suspense, useEffect } from 'react';
import { BrowserRouter, useRoutes, Routes, Route } from 'react-router-dom';
import { Toaster } from 'sonner';
import { ConfirmHost } from './components/ui/ConfirmHost';
import { Layout } from './components/Layout';
import { Login } from './components/Login';
import { ThemeProvider } from './contexts/ThemeContext';
import { NotificationsProvider } from './contexts/NotificationsContext';
import { useAuth } from './hooks/useAuth';
import { TasksProvider } from './contexts/TasksContext';
import { AppRoutes } from './routes';
import { PublicBookingPage } from './pages/PublicBookingPage';
import { SubscriptionGuard } from './components/auth/SubscriptionGuard';
import { SubscriptionExpiredPage } from './pages/SubscriptionExpiredPage';
import { UpdatePasswordModal } from './components/UpdatePasswordModal';
import { LabelPrint } from './pages/LabelPrint';
import { SignupPage } from './pages/SignupPage';
import { LegalPage } from './pages/LegalPage';
import { AcmDoorPublicViewer } from './pages/AcmDoorPublicViewer';
import { GamifiedLeaderboard } from './components/leaderboard/GamifiedLeaderboard';
import { InstallAppModal } from './components/InstallAppModal';


// Declarado fora do render para não recriar (e remontar) a página a cada renderização
const AcmPrintPage = React.lazy(() => import('./pages/AcmGeneratorPage').then(m => ({ default: m.AcmGeneratorPage })));
// Calculadora de perfis da Day Brasil: login próprio, fora do CRM
const EsquadriaPublicViewer = React.lazy(() => import('./pages/EsquadriaPublicViewer').then(m => ({ default: m.EsquadriaPublicViewer })));
const DayBrasilPage = React.lazy(() => import('./pages/DayBrasilPage'));

function AppContent() {
  const { user, isAuthenticated, loading, error, company } = useAuth();

  // Injeção do Tema Dinâmico
  useEffect(() => {
    if (company) {
      const theme = company.parent_settings?.theme || company.settings?.theme;
      const root = document.documentElement;
      
      if (theme) {
        if (theme.primaryColor) root.style.setProperty('--primary-color', theme.primaryColor);
        if (theme.secondaryColor) root.style.setProperty('--secondary-color', theme.secondaryColor);
        // Pode ser expandido para injetar fontes, etc.
      } else {
        // Limpar se não houver tema
        root.style.removeProperty('--primary-color');
        root.style.removeProperty('--secondary-color');
      }
    }
  }, [company]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-slate-800 dark:to-gray-900 flex items-center justify-center" role="status">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" aria-hidden="true"></div>
          <p className="text-gray-600 dark:text-gray-300 text-lg">
            Carregando...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-slate-800 dark:to-gray-900 flex items-center justify-center p-4">
        <div className="text-center bg-white/70 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-8 shadow-lg border border-white/20 dark:border-gray-700 max-w-md" role="alert">
          <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <span className="text-red-700 text-2xl">⚠️</span>
          </div>
          <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-2">Erro de Autenticação</h2>
          <p className="text-gray-600 dark:text-gray-300 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return <Login />;
  }

  return (
    <SubscriptionGuard>
      <Layout>
        <Suspense fallback={
          <div className="flex items-center justify-center h-full min-h-[50vh]">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        }>
          <AppRoutes />
        </Suspense>
      </Layout>
      <InstallAppModal />
    </SubscriptionGuard>
  );
}

import { GlobalChatProvider } from './contexts/GlobalChatContext';
import { ServiceNotesProvider } from './contexts/ServiceNotesContext';

function App() {
  return (
    // O QueryClient vem de main.tsx (com cache de 5 min e sem refetch ao trocar de aba).
    // Um segundo provider aqui anulava essa configuração.
    <BrowserRouter>
        <ThemeProvider>
          <GlobalChatProvider>
            <ServiceNotesProvider>
            <NotificationsProvider>
              <TasksProvider>
                <Routes>
                  <Route path="/subscription-expired" element={<SubscriptionExpiredPage />} />
                  <Route path="/assinar" element={<SignupPage />} />
                  <Route path="/termos" element={<LegalPage doc="terms" />} />
                  <Route path="/privacidade" element={<LegalPage doc="privacy" />} />
                  <Route path="/book/:slug" element={<PublicBookingPage />} />
                  <Route path="/print/label/:id" element={<LabelPrint />} />
                  <Route path="/leaderboard" element={<GamifiedLeaderboard />} />
                  <Route path="/door-view/:token" element={<AcmDoorPublicViewer />} />
                  <Route path="/esquadria-view/:token" element={
                    <Suspense fallback={<div>Carregando...</div>}>
                      <EsquadriaPublicViewer />
                    </Suspense>
                  } />
                  <Route path="/print-acm" element={
                    <Suspense fallback={<div>Carregando...</div>}>
                      <AcmPrintPage />
                    </Suspense>
                  } />
                  <Route path="/daybrasil" element={
                    <Suspense fallback={<div className="min-h-screen bg-slate-100 dark:bg-slate-950" />}>
                      <DayBrasilPage />
                    </Suspense>
                  } />
                  <Route path="*" element={<AppContent />} />


                </Routes>
                <Toaster position="top-right" richColors duration={4000} visibleToasts={3} offset={{ top: 76, right: 16 }} style={{ zIndex: 45 }} />
                <ConfirmHost />
                <UpdatePasswordModal />
              </TasksProvider>
            </NotificationsProvider>
            </ServiceNotesProvider>
          </GlobalChatProvider>
        </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
